/**
 * cameraRigInsert — add the Orbit Camera Rig to the LIVE event graph from outside the editor.
 *
 * The editor inserts rigs through EventGraph's addNodeAt('rigOrbit', …), which flows into React
 * state and is persisted on save. But CameraRigNotice's one-click fix runs in LIVE mode, where
 * the BlueprintEditor (and its id counter) is unmounted — so it mutates the shared
 * `eventGraphState` directly instead. EventRuntime.fireEvent reads `eventGraphState` fresh on
 * every fire (see EventRuntime.js), so drag/wheel start driving the camera on the next input;
 * the group's own inner beginPlay (rig limits) applies on the next full load. This is an
 * in-session unstick — durable persistence is the editor save or the server-side backfill.
 *
 * Kept out of cameraRigTemplates.js on purpose: that module is deliberately state-free (pure,
 * directly unit-testable editor content). This one is the state-coupled glue.
 */

import { CAMERA_RIG_TEMPLATES, instantiateRigAsGroup } from './cameraRigTemplates.js'
import { eventGraphState, notifyEventGraphChange, notifyEventGraphInsert } from './eventGraphState.js'

/**
 * Collision-safe id generator: scans the current graph (top level, one level into graphGroup
 * containers, and function bodies) for the highest `ev_<n>` and mints above it. Mirrors
 * EventGraph's window.__evNodeId__ advance, but self-contained so it is correct in live mode
 * where that counter was never initialised.
 */
function makeNextId() {
  let max = 0
  const bump = (id) => { const m = /^ev_(\d+)$/.exec(id ?? ''); if (m) max = Math.max(max, parseInt(m[1], 10)) }
  for (const n of eventGraphState.nodes ?? []) {
    bump(n.id)
    if (n.type === 'graphGroup') for (const inner of n.data?.innerNodes ?? []) bump(inner.id)
  }
  for (const fn of Object.values(eventGraphState.functions ?? {})) {
    for (const n of fn.nodes ?? []) bump(n.id)
  }
  return () => `ev_${++max}`
}

/**
 * Splice the Orbit Camera Rig group into the live event graph and notify subscribers (so
 * CameraRigNotice re-reads and hides). Returns the inserted group node.
 */
export function addOrbitRigToLiveGraph(position = { x: 80, y: 80 }) {
  const group = instantiateRigAsGroup(CAMERA_RIG_TEMPLATES.orbit, position, makeNextId())
  // Direct mutation drives the runtime immediately (and is the ONLY path in the editor-less
  // standalone viewer). notifyEventGraphInsert then hands the node to a mounted editor so it
  // lands in React state — the source that flows back down here, so the editor both shows the
  // node and stops clobbering it on the next sync.
  eventGraphState.nodes = [...(eventGraphState.nodes ?? []), group]
  notifyEventGraphChange()
  notifyEventGraphInsert(group)
  return group
}
